import { useEffect } from "react";

const upsertMeta = (attr, key, content) => {
  if (content === undefined || content === null || content === "") return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
};

const upsertLink = (rel, href, extra = {}) => {
  if (!href) return;
  const selectorAttrs = Object.entries(extra)
    .map(([k, v]) => `[${k}="${v}"]`)
    .join("");
  const selector = `link[rel="${rel}"]${selectorAttrs}`;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    Object.entries(extra).forEach(([k, v]) => el.setAttribute(k, v));
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const clearMarked = (selector) => {
  document.head.querySelectorAll(selector).forEach((el) => el.remove());
};

const OG_IMAGE = "/brand/netovibe-logo.png";
const SITE_NAME = "NetoVibe";

/**
 * useSEO — master-level per-page SEO injector.
 * @param {Object} p
 * @param {string} p.title            <title> and og:title
 * @param {string} p.description      meta description + og:description
 * @param {string} p.path             canonical path (e.g. "/pricing")
 * @param {string} p.lang             "en" | "de"
 * @param {string[]} [p.keywords]     meta keywords + additional signals
 * @param {Object[]} [p.jsonLd]       array of JSON-LD objects to inject
 * @param {string} [p.robots]         meta robots directive (default: index,follow…)
 * @param {string} [p.ogImage]        OG image path (absolute or /-relative)
 */
export const useSEO = ({
  title,
  description,
  path,
  lang,
  keywords = [],
  jsonLd = [],
  robots,
  ogImage,
}) => {
  const kwKey = Array.isArray(keywords) ? keywords.join("|") : "";
  const ldKey = (() => {
    try { return JSON.stringify(jsonLd); } catch (e) { return ""; }
  })();

  useEffect(() => {
    if (title) document.title = title;

    upsertMeta("name", "description", description);
    upsertMeta(
      "name",
      "keywords",
      Array.isArray(keywords) && keywords.length ? keywords.join(", ") : null,
    );
    upsertMeta(
      "name",
      "robots",
      robots || "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
    );
    upsertMeta("name", "author", SITE_NAME);
    upsertMeta("name", "application-name", SITE_NAME);
    upsertMeta("name", "generator", SITE_NAME);

    // Open Graph
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const cleanPath = path && path.startsWith("/") ? path : `/${path || ""}`;
    const canonical = `${origin}${cleanPath}`;
    const img = ogImage || OG_IMAGE;
    const absImg = img.startsWith("http") ? img : `${origin}${img}`;

    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", absImg);
    upsertMeta("property", "og:image:alt", `${SITE_NAME} — ${title || ""}`);
    upsertMeta("property", "og:locale", lang === "de" ? "de_DE" : "en_US");
    upsertMeta(
      "property",
      "og:locale:alternate",
      lang === "de" ? "en_US" : "de_DE",
    );

    // Twitter card
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", absImg);
    upsertMeta("name", "twitter:image:alt", `${SITE_NAME} logo`);

    // Canonical
    upsertLink("canonical", canonical);

    // hreflang alternates (idempotent)
    clearMarked('link[rel="alternate"][data-netovibe="1"]');
    ["en", "de"].forEach((l) => {
      const link = document.createElement("link");
      link.setAttribute("rel", "alternate");
      link.setAttribute("hreflang", l);
      link.setAttribute(
        "href",
        `${canonical}${cleanPath.includes("?") ? "&" : "?"}lang=${l}`,
      );
      link.setAttribute("data-netovibe", "1");
      document.head.appendChild(link);
    });
    const xDefault = document.createElement("link");
    xDefault.setAttribute("rel", "alternate");
    xDefault.setAttribute("hreflang", "x-default");
    xDefault.setAttribute("href", canonical);
    xDefault.setAttribute("data-netovibe", "1");
    document.head.appendChild(xDefault);

    // JSON-LD structured data (per-page bundle)
    clearMarked('script[type="application/ld+json"][data-netovibe="page"]');
    if (Array.isArray(jsonLd) && jsonLd.length) {
      jsonLd.forEach((obj) => {
        if (!obj) return;
        const s = document.createElement("script");
        s.type = "application/ld+json";
        s.setAttribute("data-netovibe", "page");
        try {
          s.textContent = JSON.stringify(obj);
          document.head.appendChild(s);
        } catch (e) {
          /* no-op */
        }
      });
    }
  }, [title, description, path, lang, kwKey, ldKey, robots, ogImage]); // eslint-disable-line react-hooks/exhaustive-deps
};
